import React, { useState, useEffect } from 'react';
import PageRenderer from '../components/renderer/PageRenderer';
import SettingsLoader from '../components/common/SettingsLoader';
import { useAuth0 } from '@auth0/auth0-react';
import { apiPath } from "../config";
import { useParams, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { Container, Row } from 'react-bootstrap';
import SimpleHeader from '../components/SimpleHeader';
import MainAppWrap from '../Layout/MainAppWrap';
import Footer from '../components/Footer';

interface Schema {
    header?: {
        logo_url?: string;
        title?: string;
    };
    tabs?: Array<{
        name?: string;
        id?: string;
        title?: string;
        card_list?: Array<{
            title?: string;
            [key: string]: any;
        }>;
        [key: string]: any;
    }>;
    [key: string]: any;
}

interface DealRoomProps {
    id?: string;
}

interface EptMetaData {
    deal_room_id?: string;
    active_tab?: string | null;
}

const DealRoom: React.FC<DealRoomProps> = ({ id }) => {
    const [searchParams] = useSearchParams();
    const [just_created, setJustCreated] = useState<boolean>(searchParams.get('just_created') === 'true');
    const { dealroom_id } = useParams<{ dealroom_id: string }>();
    const { user, getIdTokenClaims } = useAuth0();
    const [schema, setSchema] = useState<Schema | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
    const [eptMetaData, setEptMetaData] = useState<EptMetaData | null>(null);
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [showChat, setShowChat] = useState<boolean>(true);
    const [chatWidth, setChatWidth] = useState<number>(() => {
        // Load stored width from localStorage on initial mount
        const storedWidth = localStorage.getItem('ept-chat-width');
        return storedWidth ? parseInt(storedWidth, 10) : 400;
    });
    const [updatedTabs, setUpdatedTabs] = useState<Set<string>>(new Set());

    // Handle chat width change from EptSystemChat component
    const handleChatWidthChange = (newWidth: number): void => {
        setChatWidth(newWidth);
    };

    const getDealRoomsSchema = async (): Promise<void> => {
        try {
            const getidToken = await getIdTokenClaims();
            const idToken = getidToken?.__raw;
            const options: RequestInit = {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    "Content-Type": "application/json",
                },
            };
            const path = `/admin/v1/deal_room/admin/core/${dealroom_id}`;
            const url = `${apiPath()}${path}`;
            const response = await fetch(url, options);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();
            if (result.tabs && result.tabs.length > 0 && result.tabs[0].card_list && result.tabs[0].card_list.length > 0) {
                if (result.tabs[0].card_list[0].title === "Loading...") {
                    setTimeout(() => {
                        getDealRoomsSchema();
                    }, 10000);
                }
            }
            setSchema(result);
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching deal rooms schema:', error);
        }
    };

    useEffect(() => {
        if (user && schema === null) {
            getDealRoomsSchema();
        }
        if (dealroom_id) {
            setEptMetaData({
                deal_room_id: dealroom_id,
                active_tab: activeTab,
            });
        }
    }, [user, dealroom_id, activeTab]);

    const clearTabData = (tab: string): void => {
        setSchema(prevSchema => {
            if (!prevSchema || !prevSchema.tabs) return prevSchema;
            
            // Find and update the matching tab to remove card_list
            const updatedTabs = prevSchema.tabs.map(tabItem => {
                // Match by tab name/id - assuming tab parameter is the tab identifier
                if (tabItem.name === tab || tabItem.id === tab || tabItem.title === tab) {
                    // Remove card_list from this tab
                    const { card_list, ...tabWithoutCardList } = tabItem;
                    return tabWithoutCardList;
                }
                return tabItem;
            });
            
            return { ...prevSchema, tabs: updatedTabs };
        });
    };
    
    // Function to clear tab update indicator when tab is viewed
    const clearTabUpdateIndicator = (tabId: string): void => {
        setUpdatedTabs(prev => {
            const newSet = new Set(prev);
            newSet.delete(tabId);
            return newSet;
        });
    };
    
    const mainContentClassName = `main-content${isChatOpen ? ' chat-open' : ''}`;
    const mainContentStyle = isChatOpen ? { marginRight: `${chatWidth}px` } : {};
    const chatPanelStyle = { 
        width: `${chatWidth}px`,
        right: isChatOpen ? '0' : `-${chatWidth}px`
    };

    return (
        <div>
            <MainAppWrap>
                <div className={mainContentClassName} style={mainContentStyle}>
                    {/* Header Section */}
                    {schema?.header ? (
                        <SimpleHeader logo={schema?.header?.logo_url} title={schema?.header?.title} />
                    ) : (
                        <SimpleHeader />
                    )}
                    <Container fluid="xxl" className="">
                        <Row>
                            {/* <button onClick={() => clearTabData("account_intel")}>Clear</button> */}
                            <div className="page-container">
                                {isLoading ? <SettingsLoader /> : <PageRenderer 
                                    schema={schema} 
                                    activeTab={activeTab} 
                                    setActiveTab={setActiveTab}
                                    updatedTabs={updatedTabs}
                                    clearTabUpdateIndicator={clearTabUpdateIndicator}
                                />}
                            </div>
                        </Row>
                    </Container>
                </div>
                <ToastContainer />
                <Footer />
            </MainAppWrap>
        </div>
    );
};

export default DealRoom;

